// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + white inner surface with subtle blue border
// - Left orange accent stripe section
// - Inputs + selects with soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Keeps your transport logic intact (only minor safe UX tweaks: close snackbar, show helper text)

import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, API_UPLOAD_URL } from "./config";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  FormHelperText,
  Divider,
} from "@mui/material";

const TransportApplication = () => {
  // ✅ Theme constants (same palette)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  // ✅ shared sx (same structure as your TimeOff page)
  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const [StudentID, setStudentID] = useState("");
  const [studentkey, setStudentKey] = useState("");
  const [StopID, setStopID] = useState("");
  const [ApplicationStatus, setApplicationStatus] = useState(null);
  const [stops, setStops] = useState([]);
  const [logincampusCode, setCampusCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const token = localStorage.getItem("auth");

  // **Fetch and decode CampusCode from JWT**
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const username = decodedToken.username;
        const loggedCampusCode = decodedToken.campusCode;

        setCampusCode(loggedCampusCode);
        setStudentID(username);
        setStudentKey(decodedToken.studentkey);
      } catch (err) {
        console.error("Error decoding token:", err);
        setError("Failed to decode authentication token");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // **Fetch available bus stops**
  useEffect(() => {
    if (!logincampusCode) return;

    const fetchStops = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/availableStops`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStops(response.data || []);
      } catch (err) {
        console.error("Error fetching bus stops:", err);
        setError("Failed to fetch bus stops");
      }
    };

    fetchStops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode]);

  // **Check Application Status**
  const checkApplicationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/transportApplicationStatus`, {
        params: {
          StudentID: StudentID,
          StudentKey: studentkey,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplicationStatus(response.data);
      setError("");
    } catch (error) {
      console.error("Error fetching status", error);
      setError("Failed to fetch transport application status");
    }
  };

  // **Apply for Transport**
  const applyForTransport = async () => {
    if (!StopID) {
      setError("Please select a bus stop.");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/applyTransport`,
        {
          StudentID: StudentID,
          StudentKey: studentkey,
          StopID: StopID,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      setError("");
      checkApplicationStatus();
    } catch (error) {
      console.error("Error applying for transport", error);

      if (error.response) {
        if (error.response.status === 400) {
          setError(error.response.data.message || "All fields are required");
        } else if (error.response.status === 404) {
          setError(error.response.data.message || "Student has already applied for transport");
        } else {
          setError(`Error: ${error.response.data.message || "Something went wrong."}`);
        }
      } else if (error.request) {
        setError("No response from server. Please try again.");
      } else {
        setError("An unexpected error occurred.");
      }
    }
  };

  const closeSnackbar = () => setSuccess(false);

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Transport Application
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Apply for bus transport and track approval status
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Main surface with left orange stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Application Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : null}

              <Snackbar open={success} autoHideDuration={2500} onClose={closeSnackbar}>
                <Alert severity="success" onClose={closeSnackbar} sx={{ width: "100%" }}>
                  Transport Application Submitted!
                </Alert>
              </Snackbar>

              <Grid container spacing={2}>
                {/* Student ID */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Username"
                    value={StudentID}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                {/* Stop Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={inputSx} error={!StopID && !!error}>
                    <InputLabel id="stop-label">Bus Stop</InputLabel>
                    <Select
                      labelId="stop-label"
                      label="Bus Stop"
                      value={StopID}
                      onChange={(e) => setStopID(e.target.value)}
                    >
                      <MenuItem value="" disabled>
                        Select Bus Stop
                      </MenuItem>
                      {stops.map((stop) => (
                        <MenuItem key={stop.StopID} value={stop.StopID}>
                          {stop.StopName}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{!StopID && error ? "Please select a bus stop." : " "}</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Actions */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button variant="contained" sx={primaryBtnSx} onClick={applyForTransport}>
                      Apply for Transport
                    </Button>
                    <Button variant="outlined" sx={outlinedBtnSx} onClick={checkApplicationStatus}>
                      Check Application Status
                    </Button>
                  </Box>
                </Grid>

                {/* Status */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                    Application Status
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Application Date"
                    value={
                      ApplicationStatus?.ApplicationDate
                        ? new Date(ApplicationStatus.ApplicationDate).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          })
                        : ""
                    }
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Status Updated Date"
                    value={
                      ApplicationStatus?.ApprovedDate
                        ? new Date(ApplicationStatus.ApprovedDate).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          })
                        : ""
                    }
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Status"
                    value={ApplicationStatus?.ApplicationStatus || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Transport Fee"
                    value={ApplicationStatus?.TransportFee || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Stop Name"
                    value={ApplicationStatus?.StopName || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    label="Bus Number"
                    value={ApplicationStatus?.BusNumber || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    label="Driver Name"
                    value={ApplicationStatus?.DriverName || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    label="Stop Time"
                    value={ApplicationStatus?.StopTime || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    label="License Plate"
                    value={ApplicationStatus?.LicensePlate || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default TransportApplication;
